import ArrowLeft from '@betfan/icons/linear/Arrows/AltArrowLeft.svg?react';
import { Modal } from '@material-ui/core';
import { AuthologicApi } from 'api';
import { ArrowLeftIcon } from 'assets/icons';
import CloseIcon from 'assets/images/closeIconGray.svg?react';
import { DSButton, DSTag } from 'designSystem';
import { RegistrationBanners } from 'features/Home';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RegisterEvents, setDataLayer, setDataLayerWithCallback, ThirdPartyRegistrationEvents } from 'shared/constants/datalayer';
import { useDeviceDetection } from 'shared/hooks/useDeviceDetection';
import { useNewMobileUiManager } from 'shared/hooks/useNewMobileUiManager';
import { isLoggedin } from 'shared/util/utility';
import { APP_URL, SectionNavigation } from '../../shared/constants/common';
import { FeatureLoader, useMojeIdFeatureFlag } from '../../shared/featureFlags';
import useSectionNavigation from '../../shared/hooks/useSectionNavigation';
import { State } from '../../shared/interface';
import { showRegisterdModal } from '../registration/store/register.action';

const RegistrationMethod: React.FC = () => {
	const dispatch = useDispatch();

	const device = useDeviceDetection();
	const isMobile = device === 'handheld';

	const history = useHistory();
	const location = useLocation();
	const { isViewActive, goToFirstNavigationView } = useSectionNavigation();

	const { t } = useTranslation();

	const banners = useSelector<State, RegistrationBanners>((state) => state.home.registrationBanners);
	const [useSmallMobileBanner, setUseSmallMobileBanner] = useState<boolean>(false);
	const imageContainerRef = useRef<HTMLDivElement>(null);

	const mojeIdFeatureFlag = useMojeIdFeatureFlag();
	const showNewUI = useNewMobileUiManager();
	useEffect(() => {
		if (!mojeIdFeatureFlag.value) {
			openRegistrationForm();
		}
	});

	useEffect(() => {
		if (!imageContainerRef.current) {
			return;
		}

		const resizeObserver = new ResizeObserver(() => {
			if (!imageContainerRef.current) {
				return;
			}

			// Empirical 250px threshold for switching between MOBILE and SMALL_MOBILE banners
			setUseSmallMobileBanner(imageContainerRef.current.clientHeight < 250);
		});
		resizeObserver.observe(imageContainerRef.current);
		return () => resizeObserver.disconnect();
	}, []);

	useEffect(() => {
		if (isLoggedin()) {
			history.replace({ pathname: APP_URL.home });
			return;
		}

		if (isMobile) {
			if (isViewActive(SectionNavigation.REGISTRATION_METHOD)) {
				history.replace({ pathname: APP_URL.register });
			}
		} else {
			const pathname = location.pathname;
			if (pathname === APP_URL.register || pathname === `${APP_URL.register}/`) {
				history.replace({ pathname: APP_URL.home, search: location.search, hash: `section=${SectionNavigation.REGISTRATION_METHOD}` });
			}
		}
	}, [isMobile]);

	const handleClose = () => {
		if (isMobile) {
			if (location.state) {
				history.goBack();
			} else {
				history.push(APP_URL.home);
			}
		} else {
			history.push({ ...location, hash: '' });
		}
	};

	const initializeMojeIdRegistration = async () => {
		if (isLoggedin()) {
			history.push(APP_URL.home);
			return;
		}

		const response = await AuthologicApi.registrationMojeId.initialize();

		setDataLayerWithCallback(ThirdPartyRegistrationEvents.REGISTER_THIRD_PARTY_IN(response.data.id, 'mojeID')).finally(() => {
			window.location.href = response.data.url;
		});
	};

	const openRegistrationForm = () => {
		if (isLoggedin()) {
			history.push(APP_URL.home);
			return;
		}

		if (isMobile) {
			history.push(APP_URL.registrationForm);
		} else {
			goToFirstNavigationView(SectionNavigation.REGISTRATION);
			dispatch(showRegisterdModal());
		}
		setDataLayer(RegisterEvents.DATALAYER_REGISTER_START('manual'));
	};

	const buttons = (
		<>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<div className="text-h3 font-bold">{t('registrationMethods_openFullAccount')}</div>
					<div className="text-paragraph-sm font-medium">{t('registrationMethods_registrationWithoutDocuments')}</div>
				</div>
				<div>
					<DSButton
						size="medium"
						color="primary"
						className="relative w-full"
						onClick={initializeMojeIdRegistration}
						cyTestRef="registration-moje-id"
					>
						<span className="text-paragraph-sm">{t('registrationMethods_mojeId')}</span>

						{t('registrationMethods_new') && (
							<DSTag size="extra-small" color="success" className="absolute right-2 top-1/2 -translate-y-1/2">
								{t('registrationMethods_new')}
							</DSTag>
						)}
					</DSButton>
				</div>
			</div>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col gap-2">
					<div className="text-h3 font-bold">{t('registrationMethods_openTemporaryAccount')}</div>
					<div className="text-paragraph-sm font-medium">{t('registrationMethods_verificationNeeded')}</div>
				</div>
				<div>
					<DSButton size="medium" color="tertiary" className="w-full" onClick={openRegistrationForm} cyTestRef="registration-form">
						{t('registrationMethods_form')}
					</DSButton>
				</div>
			</div>
		</>
	);

	if (isMobile) {
		return (
			<>
				<div className="fixed bg-white inset-0 z-overlay w-full h-full flex flex-col justify-between overflow-auto bg-no-repeat bg-cover bg-center">
					<div className="bg-primary-500 text-white flex items-center relative pt-safe px-2">
						{showNewUI ? (
							<div className="py-4 relative w-full">
								<button className="flex items-center gap-2 relative z-[1]" onClick={handleClose} data-cy="back-button">
									<ArrowLeftIcon />
									<div className="font-normal">{t('quit')}</div>
								</button>
								<div className="font-semibold absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center">
									{t('registrationMethods_welcome')}
								</div>
							</div>
						) : (
							<div className="py-4 flex items-center">
								<button onClick={handleClose} data-cy="back-button">
									<ArrowLeft className="h-8" />
								</button>
								<div className="ml-3 font-medium">{t('registrationMethods_welcome')}</div>
							</div>
						)}
					</div>

					<div
						className="w-full h-[calc(100%+5px)] -mb-2 overflow-auto bg-no-repeat bg-cover bg-center"
						ref={imageContainerRef}
						style={{
							backgroundImage: `url(${useSmallMobileBanner ? banners.SMALL_MOBILE : banners.MOBILE})`,
						}}
					/>

					<div className="bg-white dark:bg-dark-200 p-4 pb-6 rounded-t-8">
						<div className="flex flex-col gap-8 pb-safe">{buttons}</div>
					</div>
				</div>
			</>
		);
	}

	return (
		<>
			<Modal
				className="overflow-hiddden flex items-center justify-center backdrop-blur-sm"
				open={true}
				onClose={handleClose}
				BackdropProps={{ className: '!bg-black !bg-opacity-75' }}
			>
				<div className="relative w-full max-w-modal-lg focus:outline-none">
					<div>
						<img src={banners.DESKTOP} alt="" className="w-full rounded-t-8" />
					</div>
					<div className="px-4 py-6 rounded-8 bg-white dark:bg-dark-200 -translate-y-2">
						<div className="grid grid-cols-2 gap-16">{buttons}</div>
					</div>
					<button onClick={handleClose} className="absolute top-0 right-0 p-4 text-white">
						<CloseIcon className="w-3 h-3" />
					</button>
				</div>
			</Modal>
		</>
	);
};

export const RegistrationMethodContainer = () => (
	<FeatureLoader>
		<RegistrationMethod />
	</FeatureLoader>
);
